import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Mic,
  Send,
  Paperclip,
  Brain,
  Lightbulb,
  ShieldCheck,
  User,
  X,
  Save,
  Clock,
  Trash2,
  RotateCcw,
  Plus,
  MessageSquarePlus,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import "./SymptomChat.css";
import InteractionCard from "../components/interaction_card";
import FeatureHeader from "../components/FeatureHeader";
import {useAuth} from "../contexts/AuthContext"
import nine from "../assets/icons/9.png"
// simple collapsible for long recommendation sections
const CollapsibleSection = ({ title, children, threshold = 3 }) => {
  const [open, setOpen] = useState(false);
  const itemCount = React.Children.count(children);
  if (itemCount <= threshold) {
    return (
      <div>
        <strong>{title}</strong>{children}
      </div>
    );
  }
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: 'none',
          color: '#667eea',
          cursor: 'pointer',
          padding: 0,
          fontSize: '0.9rem',
          textDecoration: 'underline',
        }}
      >
        {open ? `Hide ${title}` : `Show ${title}`}
      </button>
      {open && <div style={{ marginLeft: '1rem' }}>{children}</div>}
    </div>
  );
};

const BodyPainSymptomIcon = () => (
  <svg viewBox="0 0 512 512" className="chip-icon-svg" aria-hidden="true" focusable="false">
    <g fill="currentColor">
      <path d="m290.308 90.5c0-49.902-40.598-90.5-90.5-90.5s-90.5 40.598-90.5 90.5 40.598 90.5 90.5 90.5 90.5-40.598 90.5-90.5zm-90.5 60.5c-33.36 0-60.5-27.14-60.5-60.5s27.14-60.5 60.5-60.5 60.5 27.14 60.5 60.5-27.14 60.5-60.5 60.5z"></path>
      <path d="m307.808 181c-11.965 0-203.591 0-215.574 0-31.665 0-57.426 25.761-57.426 57.426 0 31.842 23.514 45.475 30 53.787v204.787c0 8.284 6.716 15 15 15h270c8.284 0 15-6.716 15-15v-259c0-31.43-25.571-57-57-57zm-234.967 76.82c-17.234-17.236-4.881-46.82 19.393-46.82h131.311l-54.367 54.367-35.876-32.053c-6.177-5.52-15.66-4.985-21.18 1.192-5.519 6.178-4.985 15.66 1.192 21.18l46.45 41.5c5.934 5.301 14.975 5.047 20.6-.58l64.749-64.749c6.5-6.499 17.133-6.558 23.704-.131 6.713 6.566 6.773 17.328.133 23.968l-77.861 77.861c-11.721 11.721-30.792 11.721-42.512 0zm261.967 224.18h-30v-166c0-8.284-6.716-15-15-15s-15 6.716-15 15v166h-180v-159.787l32.556 32.556c23.47 23.471 61.464 23.475 84.938 0l77.861-77.861c18.186-18.186 18.319-47.575.353-65.908h17.292c14.888 0 27 12.112 27 27z"></path>
      <path d="m388.352 86.401c-7.208-4.082-16.362-1.547-20.443 5.663l-7.815 13.804-9.705-12.55c-6.535-8.451-19.627-7.559-24.918 1.785l-18.935 33.438c-4.082 7.209-1.547 16.362 5.662 20.444 7.191 4.072 16.353 1.563 20.444-5.662l7.818-13.806 9.706 12.551c6.543 8.462 19.634 7.548 24.919-1.786l18.932-33.438c4.08-7.209 1.544-16.362-5.665-20.443z"></path>
      <path d="m476.939 205.625c-1.507-8.146-9.333-13.531-17.479-12.021l-15.598 2.886 3.375-15.501c2.274-10.445-6.83-19.888-17.385-17.941l-37.786 6.989c-8.146 1.507-13.528 9.332-12.022 17.478 1.507 8.146 9.333 13.526 17.478 12.022l15.602-2.886-3.376 15.502c-2.274 10.446 6.841 19.896 17.386 17.941l37.784-6.991c8.147-1.507 13.528-9.332 12.021-17.478z"></path>
    </g>
  </svg>
);

const FeverSymptomIcon = () => (
  <svg viewBox="0 0 511.617 511.617" className="chip-icon-svg" aria-hidden="true" focusable="false">
    <g fill="currentColor">
      <path d="m434.871 193.047-31.65-27.5c-5.443-4.852-14.233-4.853-19.676 0l-31.651 27.5c-6.254 5.434-6.919 14.908-1.485 21.161 2.966 3.414 7.137 5.162 11.33 5.162 3.488 0 6.992-1.21 9.832-3.677l6.813-5.919v86.034c0 8.284 6.716 15 15 15s15-6.716 15-15v-86.033l6.812 5.919c6.253 5.434 15.729 4.769 21.161-1.485s4.767-15.729-1.486-21.162z"></path>
      <path d="m262.278 364.527v-243.527c0-8.284-6.716-15-15-15s-15 6.716-15 15v243.527c-17.368 6.185-29.839 22.785-29.839 42.25 0 24.725 20.115 44.84 44.839 44.84s44.839-20.115 44.839-44.84c0-19.466-12.471-36.066-29.839-42.25zm-15 57.089c-8.182 0-14.839-6.657-14.839-14.84s6.657-14.839 14.839-14.839 14.839 6.656 14.839 14.839-6.657 14.84-14.839 14.84z"></path>
      <path d="m322.278 74.999c0-41.354-33.645-74.999-75-74.999s-75 33.645-75 74.999v258.593c-19.04 19.526-29.839 45.829-29.839 73.185 0 28.735 11.434 55.561 32.195 75.533 19.739 18.99 45.374 29.308 72.588 29.308 1.39 0 2.787-.027 4.184-.081 55.14-2.135 99.364-47.034 100.68-102.219.676-28.329-10.119-55.558-29.809-75.729v-258.59zm-72.032 406.559c-20.542.796-39.999-6.615-54.813-20.867-14.828-14.265-22.994-33.412-22.994-53.914 0-21.157 9.064-41.44 24.868-55.65 3.164-2.845 4.971-6.899 4.971-11.154v-264.974c0-24.812 20.187-44.999 45-44.999s45 20.187 45 44.999v264.974c0 4.255 1.807 8.311 4.972 11.155 16.311 14.663 25.367 35.612 24.846 57.475-.94 39.385-32.5 71.432-71.85 72.955z"></path>
      <path d="m126.584 280.808h-40c-8.284 0-15 6.716-15 15s6.716 15 15 15h40c8.284 0 15-6.716 15-15 0-8.285-6.715-15-15-15z"></path>
      <path d="m126.584 220.808h-40c-8.284 0-15 6.716-15 15s6.716 15 15 15h40c8.284 0 15-6.716 15-15s-6.715-15-15-15z"></path>
      <path d="m126.584 160.808h-40c-8.284 0-15 6.716-15 15s6.716 15 15 15h40c8.284 0 15-6.716 15-15s-6.715-15-15-15z"></path>
      <path d="m126.584 100.808h-40c-8.284 0-15 6.716-15 15s6.716 15 15 15h40c8.284 0 15-6.716 15-15s-6.715-15-15-15z"></path>
    </g>
  </svg>
);

const HeadacheSymptomIcon = () => (
  <svg viewBox="0 0 512 512" className="chip-icon-svg" aria-hidden="true" focusable="false">
    <g fill="currentColor">
      <path d="m436.126 321.45-32.09-52.27c-3.86-8.97-5.81-18.46-5.81-28.25-.02-24.42-5.31-70.24-40.6-102.77-30.03-27.68-74.6-39.34-132.5-34.67-61.37 4.96-107.48 32.31-129.83 77.01-22.29 44.58-17.99 101.02 11.21 147.29 10.44 16.56 16.76 35.31 18.27 54.24 2.45 30.91 1.35 74.63-17.87 107.38-2.72 4.64-2.75 10.38-.08 15.04 2.68 4.67 7.64 7.55 13.02 7.55h184.94c9.58 0 16.72-8.88 14.64-18.25-1.48-6.67-2.75-14.03-3.81-22.02 20.91 3.11 44.69 3.2 63.76-6.03 22.3-10.78 20.3-32 17.99-56.58-1.15-12.12-2.52-26.68-2.43-44.96l21.16-2.74c.23-.03.46-.06.69-.1 19.74-3.5 27.95-25.85 19.34-39.87zm-57.81 14.61c-7.25.94-12.76 6.96-13.06 14.27-1.08 26.39.84 46.74 2.24 61.6 2.45 26.095.99 25.716-1.18 26.76-13.82 6.69-36.58 6.62-64.1-.19 0 0 0 0-.01 0-10.84-2.69-14.7-4.08-15.43-4.35.18.08.29.13.33.16-.67-.19-14.22-3.92-19.93 8.4-5.73 12.38 7.07 18.7 7.07 18.7 1.87.89 5.43 2.08 10.37 3.48.62 5.95 1.34 11.67 2.16 17.11h-143.34c12.93-34.77 13.53-73.58 11.24-102.35-1.89-23.75-9.77-47.21-22.8-67.86-23.64-37.46-27.37-82.62-9.75-117.87 17.73-35.46 54.18-56.39 105.42-60.52 49.2-3.98 86.13 5.05 109.75 26.82 26.88 24.78 30.92 61.22 30.93 80.72 0 19.99 6.64 38.06 9.7 43.06l29.64 48.27z"></path>
      <path d="m276.111 212.115h-32.642l25.409-35.975c4.779-6.767 3.168-16.126-3.599-20.906-6.769-4.779-16.126-3.169-20.906 3.599l-42.114 59.628c-7.012 9.929.118 23.653 12.252 23.653h32.641l-25.399 35.958c-4.779 6.767-3.168 16.126 3.598 20.906 6.767 4.78 16.127 3.168 20.906-3.598 1.524-2.157 41.227-58.365 42.15-59.682 6.993-9.999-.305-23.583-12.296-23.583z"></path>
      <path d="m245.315 78.645c8.284 0 15-6.716 15-15v-48.645c0-8.284-6.716-15-15-15s-15 6.716-15 15v48.645c0 8.284 6.715 15 15 15z"></path>
      <path d="m379.365 96.102 34.396-34.396c5.858-5.858 5.858-15.355 0-21.213-5.857-5.858-15.355-5.858-21.213 0l-34.396 34.396c-5.858 5.858-5.858 15.355 0 21.213 5.857 5.857 15.356 5.858 21.213 0z"></path>
      <path d="m132.477 96.102c5.858-5.858 5.858-15.355 0-21.213l-34.397-34.397c-5.857-5.858-15.355-5.858-21.213 0s-5.858 15.355 0 21.213l34.397 34.396c5.856 5.858 15.355 5.859 21.213.001z"></path>
    </g>
  </svg>
);

const CoughSymptomIcon = () => (
  <svg viewBox="0 0 53 53" className="chip-icon-svg" aria-hidden="true" focusable="false">
    <g fill="currentColor">
      <path d="m38.2334023 11.2371826c-.2119141-.0532227-.4326172-.0893555-.6523438-.1157227-1.6630859-.1938477-3.1054688.2416992-4.2929688 1.2978516-1.6112652 1.4332886-2.4313946 3.7928467-2.8500347 5.8425903-.5446167-2.1242065-.6018677-4.3693848-.0982056-6.5720825.7138672-3.1577148 1.663084-5.4418945 2.8222637-6.7885742.3603516-.418457.3134766-1.0498047-.1054688-1.4101563-.4169922-.3598633-1.0507813-.3149414-1.4101543.1054688-1.375 1.5976563-2.4707031 4.171875-3.2568359 7.6494141-1.0605469 4.6367188-.0146484 9.4589844 2.8701172 13.2304688l4.51367 5.9038086c.2617188.3398438.4580078.7241211.5839844 1.1474609l.6152344 2.0405273-1.03125-1.4458008c-.9072266-1.2841797-1.9599609-2.512207-3.1328125-3.652832l-5.5790997-5.3901367c-.4628906-.4599609-1.1875-.4584961-1.6484375-.0014648l-4.8115234 4.7617188c-1.6314697 1.6209106-3.1133423 3.3587646-4.421936 5.1644897-.0571899.008667-.1140747.0194092-.1711426.0292358l.442688-1.512085c.1123047-.4145508.3095703-.8046875.5898438-1.1655273l4.3691406-5.71875c.4824219-.6303101.9182739-1.2853394 1.3103638-1.9587402.0078125-.0162354.0211792-.0285645.0281982-.0452881 2.802124-4.8440552 3.2565918-10.6706543 1.1194458-15.7015381-.5078125-1.2075195-1.1455078-2.3041992-1.8945313-3.2597656-.3408203-.4345703-.96875-.5112305-1.4042969-.1699219-.4345703.3408203-.5107422.9697266-.1699219 1.4042969.6396484.8149414 1.1865234 1.7573242 1.6269531 2.8041992 1.4437256 3.3979492 1.5396729 7.2098389.4000854 10.7390137-.4061279-2.0914307-1.2246704-4.5508423-2.885437-6.0275879-4.4181451-3.9290581-10.3038568 1.0571994-12.447266 7.0283202-.5566406 1.1450196-7.3388673 15.4804688-3.5107424 26.3413086.6230471 1.7646484 2.2587893 2.972168 4.1757813 3.0766602 1.7880778.0851479 3.6080995-.0134354 5.3320918-.3323364.4911499.2947388 1.012146.5574341 1.5829468.7600708 4.3898211 1.4933968 9.1860189-.795433 10.7177744-5.2304688.734375-2.1264648.59375-4.4150391-.3925781-6.4375-.5319824-1.1137085-1.3278198-2.0838013-2.2862549-2.8485718-.1140747-3.8331299-.1934204-5.3394775-.2473755-5.9700317l3.777771-3.7390137 3.9482422 3.8138428c-.1447144 1.8266602-.2944336 7.2024536-.3574219 9.6700439-.0009766.0463867.0009766.0932617.0068359.1396484.8868523 7.7211075 7.4042683 10.5543976 15.0644512 10.1713867 1.8837891-.0932617 3.5625-1.3291016 4.1777344-3.074707 3.8232422-10.8642578-2.9541016-25.190918-3.5117188-26.3369141-2.3115232-6.4433593-6.3261716-7.9174804-7.5058591-8.2163085zm-17.4937726 19.5335694c.0292358.6812134.0633545 1.5911865.1022339 2.8013306-.1730347-.0722046-.3336182-.1564331-.515686-.2196655-.4859009-.1651611-.9785767-.2768555-1.4737549-.3539429.5960694-.7598267 1.227478-1.5026246 1.887207-2.2277222zm-12.7103281 16.0997314c-1.0839839-.059082-2.0449214-.7597656-2.3916011-1.7441406-3.6240236-10.2802735 3.3730465-24.7006836 3.4443355-24.8452149.0166016-.034668.03125-.0703125.0449219-.1064453 2.0089016-5.6440325 6.4465008-8.7496824 9.2509775-6.2602539 2.0240479 1.7972412 2.557373 6.151062 2.6108398 8.0336304-.3066406.5037842-.6351929.9981689-.9995117 1.4741821l-1.970459 2.5789795-1.4475098-1.6558228c1.616394-3.6935425 1.6201172-3.8181763 1.6201172-4.0588989 0-.5522461-.4472656-1-1-1-.4746094 0-.8720703.3305664-.9746094.7734375-.0927124.2652588-.5612803 1.369873-1.102479 2.6188354l-1.4297485-1.635437c-.3662109-.4174805-.9970703-.4575195-1.4111328-.0947266-.4160156.3637695-.4580078.9951172-.0947266 1.4111328l1.3254395 1.5161743-2.545166.4960327c-.5419922.1054688-.8955078.6303711-.7900391 1.1728516.1064472.5479202.6384506.8952999 1.1728516.7900391l3.6843872-.7178955 1.753602 2.0059204-1.1557627 1.5127563c-.4384766.5644531-.7529297 1.1904297-.9296875 1.8422852l-.5231934 1.7858276-5.1896973-3.5983276c-.4521484-.315918-1.077148-.2026367-1.3916011.2519531-.3144529.4541017-.2011717 1.0771485.2529299 1.3916017l1.3283076.9209595-1.4630733 1.4501343c-.3925781.3891602-.3945313 1.0219727-.0058594 1.4145508.3869596.390831 1.0193009.3961754 1.414062.0063477l1.7269287-1.7116699 2.0614014 1.4292603c-4.2357121 2.8317986-4.9936323 8.6738739-1.7154541 12.4893799-1.2144164.109802-2.3273925.1032712-3.15979.0625608zm15.3369151-8.3730469c.7548828 1.5488281.8623047 3.2944336.3027344 4.9155273-1.1786537 3.4138222-4.8720493 5.1180611-8.1699228 3.9936523-3.4655533-1.2288094-5.121376-4.8731041-4-8.1713867.9027719-2.6184807 3.3712559-4.3452148 6.0673838-4.3452148 2.4184035 0 4.7314128 1.3721772 5.7998046 3.6074219zm23.9980449 6.628418c-.3476563.9882813-1.3085938 1.6879883-2.390625 1.7421875-4.453125.21875-12.0351563-.5683594-12.9697266-8.3344727.0714111-2.7897339.1663818-5.7952271.2496338-7.7376709.743042.7970581 1.4460449 1.6193848 2.0560303 2.4842529l3.920105 5.4926147c.0083618.0136719.0185547.0253296.0275879.0386353l.003479.0048828c.0697632.1014404.1555176.1833496.2473755.2555542 1.5690269 1.0849342 4.9263153 3.4063911 4.7125854 3.258606.4525719.3132172 1.0776596.2008286 1.390625-.2543945.3144531-.4541016.2001953-1.0771484-.2539063-1.3911133l-.8994751-.6217651 1.3887329-1.2664185c.4082031-.3720703.4375-1.0043945.0654297-1.4125977s-1.0058594-.4360352-1.4130859-.0649414l-1.7293701 1.5770264-1.414917-.9780273c-.0078125-.0359497-.0025024-.0721436-.013916-.1077881l-.9851074-3.2667236 2.3861084-1.6544189 1.7269287 1.7116699c.3944664.389534 1.0268364.3847542 1.4140625-.0063477.3886719-.3925781.3867188-1.0253906-.0058594-1.4145508l-1.4630737-1.4501343 1.3283081-.9209595c.4541016-.3144531.5673828-.9375.2529297-1.3916016-.3154297-.4545898-.9394531-.5678711-1.3916016-.2519531l-4.8546753 3.3660278-.4754028-1.5764771c-.1953125-.6567383-.5039063-1.2612305-.9150391-1.793457l-1.3521729-1.7686157 1.5525513-1.7759399 3.6843872.7178955c.534668.1053123 1.0664673-.2424393 1.1728516-.7900391.1054688-.5424805-.2480469-1.0673828-.7900391-1.1728516l-2.545166-.4960327 1.3254395-1.5161743c.3632813-.4160156.3212891-1.0473633-.0947266-1.4111328-.4150391-.362793-1.0458984-.3227539-1.4111328.0947266l-1.4294434 1.6350708c-.5406494-1.2488403-1.0090942-2.3531494-1.1018066-2.617981-.1025391-.4433594-.5-.7739258-.9746094-.7739258-.5527344 0-1 .4477539-1 1 0 .2407227.0036621.3649902 1.6192627 4.0587769l-1.2470093 1.4264526-1.9191284-2.5101318c-.3051147-.3990479-.5842896-.8127441-.8419189-1.2368164.0383911-1.8348389.5529785-6.2800903 2.6114502-8.1113281.7529297-.6699219 1.6474609-.9345703 2.7294922-.8056641.1337891.015625.2685547.0361328.3964844.0683594.9394531.237793 4.1552734 1.4458008 6.1308594 7.0014648.0126953.0366211.0283203.0722656.0449219.1069336.071289.1445313 7.0644531 14.557129 3.4453124 24.8413087z"></path>
      <path d="m20.2812557 38.9901123c0-.5522461-.4970703-1.0498047-1.0498047-1.0498047s-1.0498047.4975586-1.0498047 1.0498047.4970703 1.050293 1.0498047 1.050293 1.0498047-.4980469 1.0498047-1.050293z"></path>
      <path d="m14.832036 37.2811279c-.5527344 0-1.0498047.4980469-1.0498047 1.050293s.4970703 1.0498047 1.0498047 1.0498047 1.0498047-.4975586 1.0498047-1.0498047-.4970703-1.050293-1.0498047-1.050293z"></path>
      <path d="m14.4941454 41.3206787c-.5527344 0-1.0498047.4975586-1.0498047 1.0498047s.4970703 1.050293 1.0498047 1.050293 1.0507813-.4980469 1.0507813-1.050293-.4980469-1.0498047-1.0507813-1.0498047z"></path>
      <path d="m17.0371151 42.0750732c.5527344 0 1.0498047-.4980469 1.0498047-1.050293s-.4970703-1.0498047-1.0498047-1.0498047-1.0498056.4975586-1.0498056 1.0498047.4970712 1.050293 1.0498056 1.050293z"></path>
      <circle cx="21.981" cy="40.787" r="1.05"></circle>
      <path d="m17.0869198 43.633667c-.5527344 0-1.0498047.4975586-1.0498047 1.0498047s.4970703 1.050293 1.0498047 1.050293 1.0498047-.4980469 1.0498047-1.050293-.4970703-1.0498047-1.0498047-1.0498047z"></path>
      <path d="m20.7207088 42.4378662c-.5527344 0-1.0498047.4975586-1.0498047 1.0498047s.4970703 1.050293 1.0498047 1.050293 1.0498047-.4980469 1.0498047-1.050293-.4970703-1.0498047-1.0498047-1.0498047z"></path>
    </g>
  </svg>
);

const VomitingSymptomIcon = () => (
  <svg viewBox="0 0 96 96" className="chip-icon-svg" aria-hidden="true" focusable="false">
    <g fill="currentColor">
      <path d="m51.437 4h-6.875c-21.871 0-39.664 17.793-39.664 39.666v6.873c0 10.324 4.075 20.165 11.224 27.512-1.899 1.373-2.985 3.366-2.985 5.933 0 2.464.707 4.39 2.102 5.725 1.948 1.865 4.813 2.291 8.382 2.291 1.407 0 18.376-.448 26.87-.541 13.609-.149 22.682-.264 24.667-.506 3.144-.362 5.737-2.704 6.611-5.97.702-2.623.12-5.254-1.48-7.305 6.963-7.362 10.814-16.895 10.814-27.139v-6.873c-.001-21.873-17.794-39.666-39.666-39.666zm26.114 79.856c-.408 1.524-1.547 2.608-2.9 2.763l-.019.003c-1.882.229-13.733.36-24.19.474-8.545.093-22.477.408-22.477.408-4.285.194-8.339.365-9.711-.946-.186-.181-.755-.725-.755-2.574 0-1.287.352-2.571 2.93-3.245 1.654-.458 3.674-.527 5.295-.583 1.062-.035 1.986-.075 2.597-.173 2.848-.384 5.583-3.04 6.869-4.492.95-.948 1.515-1.71 1.75-2.118.057-.096.673-1.026 4.612-4.156 1.167-.927 2.661-1.436 4.209-1.436h4.639c1.969 0 3.914.94 5.357 2.6 2.162 2.416 4.56 4.305 7.166 5.637 2.336 1.135 4.657 1.74 6.92 1.801 2.627.043 4.73.605 6.103 1.646 1.455 1.074 2.054 2.715 1.605 4.391zm-11.15-12.21c-.237.092-.878.208-1.923.211-1.937-1.059-3.78-2.504-5.445-4.362-2.252-2.592-5.399-4.079-8.632-4.079h-4.637c-2.53 0-4.987.845-6.923 2.382-4.195 3.333-5.319 4.683-5.754 5.523-.076.094-.314.341-.456.502-1.913.117-2.755-.139-3.182-.595 0-2.55 0-6.039 2.133-8.786 2.649-3.41 8.173-5.139 16.419-5.139 8.255 0 13.78 1.716 16.424 5.094 2.128 2.72 2.126 6.202 1.976 9.249zm20.337-21.107c0 9.298-3.562 17.941-9.997 24.541-1.788-.887-3.862-1.477-6.384-1.566.342-.686.558-1.463.558-2.371.001-2.997.003-7.529-3.053-11.434-3.563-4.556-10.061-6.77-19.862-6.77-9.8 0-16.298 2.233-19.865 6.825-3.052 3.927-3.05 8.461-3.05 11.506 0 1.844.895 3.349 2.356 4.081.156.089.345.144.514.219-.085.023-.192.082-.27.092-.529.085-1.264.101-2.107.131-1.488.051-3.262.182-5.004.509-7.182-6.642-11.312-15.961-11.312-25.763v-6.873c0-19.466 15.837-35.302 35.3-35.302h6.875c19.465 0 35.302 15.835 35.302 35.302v6.873z"></path>
      <path d="m29.378 35.335c-.983-.69-2.344-.464-3.042.522-.695.983-.462 2.347.522 3.042l6.422 4.542-7.249 2.235c-1.152.357-1.797 1.577-1.443 2.728.29.938 1.154 1.54 2.085 1.54.213 0 .43-.029.645-.096l10.45-3.224c.995-.306 1.716-1.138 1.881-2.166.165-1.031-.261-2.043-1.114-2.648z"></path>
      <path d="m69.969 45.677-7.249-2.235 6.422-4.542c.984-.695 1.217-2.059.522-3.042-.699-.986-2.063-1.212-3.042-.522l-9.155 6.476c-.853.602-1.28 1.614-1.116 2.645s.886 1.862 1.882 2.169l10.45 3.224c.214.067.432.096.645.096.931 0 1.796-.602 2.085-1.54.353-1.152-.291-2.372-1.444-2.729z"></path>
      <path d="m29.718 31.954c1.492 0 2.993-.336 4.382-1.007 2.887-1.393 4.937-4.068 5.481-7.156.21-1.188-.582-2.32-1.769-2.531-1.176-.194-2.32.581-2.528 1.769-.302 1.71-1.455 3.202-3.082 3.988-1.626.783-3.509.759-5.036-.064-1.059-.578-2.383-.184-2.958.879-.574 1.06-.18 2.384.88 2.957 1.429.776 3.023 1.165 4.63 1.165z"></path>
      <path d="m68.834 26.954c-1.528.823-3.409.85-5.038.061-1.626-.783-2.779-2.275-3.081-3.985-.21-1.188-1.357-1.963-2.528-1.769-1.187.21-1.979 1.343-1.769 2.531.545 3.088 2.595 5.762 5.48 7.153 1.389.674 2.892 1.01 4.384 1.01 1.606 0 3.2-.389 4.63-1.164 1.06-.573 1.455-1.897.88-2.957-.576-1.064-1.9-1.464-2.958-.88z"></path>
    </g>
  </svg>
);

const StomachPainSymptomIcon = () => (
  <svg viewBox="0 0 32 32" className="chip-icon-svg" aria-hidden="true" focusable="false">
    <g fill="currentColor">
      <path d="m2 29v-7c0-3.309 2.691-6 6-6h5c1.654 0 3-1.346 3-3v-10c0-.552.448-1 1-1s1 .448 1 1v10c0 2.757-2.243 5-5 5h-5c-2.206 0-4 1.794-4 4v7c0 .553-.448 1-1 1s-1-.447-1-1z"></path>
      <path d="m28 12c0-2.299-.628-3.42-2.17-3.868-2.189-.637-4.05 1.494-4.068 1.515-.272.318-.716.434-1.107.289-.393-.145-.655-.518-.655-.936v-6c0-.552.448-1 1-1s1 .448 1 1v3.895c1.098-.662 2.634-1.191 4.389-.683 3.611 1.05 3.611 4.616 3.611 5.788 0 9.028-4.972 14-14 14-4.362 0-6.687-2.708-7.484-3.873-.147-.213-.516-.094-.516.155v6.718c0 .553-.448 1-1 1s-1-.447-1-1v-6.718c0-1.259 1.023-2.282 2.282-2.282.752 0 1.457.373 1.885.997.767 1.122 2.545 3.003 5.833 3.003 7.962 0 12-4.037 12-12z"></path>
      <path d="m11.976 7.746c.057-.217.067-.256-.419-.708-.565-.526-1.618-1.504-.928-3.383.19-.519.766-.785 1.283-.594.519.19.784.765.594 1.283-.231.63-.107.745.413 1.229.51.474 1.363 1.268.992 2.681-.141.535-.688.854-1.221.713-.535-.14-.854-.687-.714-1.221z"></path>
      <path d="m8.627 9.864c-.059-.216-.069-.255-.717-.404-.753-.172-2.153-.494-2.495-2.465-.094-.545.271-1.062.814-1.157.546-.093 1.062.271 1.156.814.115.662.28.7.972.859.678.156 1.814.417 2.199 1.826.146.533-.168 1.083-.701 1.229-.54.145-1.084-.175-1.228-.702z"></path>
      <path d="m8.855 12.29c.393.389.396 1.022.007 1.414-.388.393-1.023.395-1.414.007-.161-.159-.191-.186-.823.009-.739.227-2.112.648-3.393-.888-.354-.424-.297-1.055.127-1.408.423-.354 1.054-.298 1.408.127.429.515.606.46 1.271.257.666-.204 1.779-.546 2.817.482z"></path>
    </g>
  </svg>
);

const SymptomChat = () => {
  const trustHighlights = [
    { Icon: Brain, title: "AI-assisted", subtitle: "Smart guidance" },
    { Icon: User, title: "Clinician-reviewed", subtitle: "By licensed experts" },
    { Icon: ShieldCheck, title: "Private & secure", subtitle: "Your data is safe" },
  ];

  // extract options from message text that contains bullet points or numbered list
  const extractOptionsFromMessage = (messageText) => {
    if (!messageText || typeof messageText !== 'string') return [];

    const normalizedText = messageText.replace(/\r/g, "");
    const lines = normalizedText.split("\n");
    const options = [];

    // Parse bullet, numbered, and checkbox list lines anywhere in the message.
    const listPattern = /^\s*(?:[-•*]|\d+[.)]|\[[ xX]?\])\s+(.+)$/;
    for (const line of lines) {
      const match = line.match(listPattern);
      if (!match) continue;
      const cleaned = match[1].replace(/[\s:.-]+$/, "").trim();
      if (cleaned && cleaned.length > 1 && cleaned.length < 200) {
        options.push(cleaned);
      }
    }

    if (options.length > 0) {
      console.log('✅ Extracted options:', options.length, 'items');
      return options;
    }

    // Fallback for inline labels like "Option 1: Fever".
    const inlinePattern = /(?:option\s*\d+\s*[:.-]\s*)([^\n,;]+)/gi;
    let inlineMatch;
    while ((inlineMatch = inlinePattern.exec(normalizedText)) !== null) {
      const cleaned = inlineMatch[1].trim();
      if (cleaned && cleaned.length > 1 && cleaned.length < 200) {
        options.push(cleaned);
      }
    }

    if (options.length > 0) {
      console.log('✅ Extracted inline options:', options.length, 'items');
      return options;
    }

    console.log('🔍 No options found in message');
    return [];
  };

  const normalizeBackendOptions = (inputOptions) => {
    if (!Array.isArray(inputOptions)) return [];
    return inputOptions
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          const candidate = item.label || item.text || item.value || item.title;
          return typeof candidate === "string" ? candidate.trim() : "";
        }
        return "";
      })
      .filter(Boolean);
  };

  const isGenericBinaryOptions = (options) => {
    if (!Array.isArray(options) || options.length === 0 || options.length > 2) return false;
    const normalized = options.map((opt) => String(opt).trim().toLowerCase());
    const genericSet = new Set([
      "yes",
      "no",
      "yes.",
      "no.",
      "yes, i have one or more of these symptoms",
      "no, i do not have any of these symptoms",
      "i have these symptoms",
      "i don't have these symptoms",
    ]);
    return normalized.every((opt) => genericSet.has(opt));
  };

  const isYesNoOptionSet = (options) => {
    if (!Array.isArray(options) || options.length !== 2) return false;
    return options.every((opt) => /^\s*(yes|no)\b/i.test(String(opt)));
  };

  const simplifyBinaryOptionLabel = (optionText) => {
    const text = String(optionText || "").trim();
    if (/^yes\b/i.test(text)) return "Yes";
    if (/^no\b/i.test(text)) return "No";
    return text;
  };

  const cleanAssistantResponseText = (text) => {
    if (typeof text !== "string") return "";
    return text
      .replace(/\r/g, "")
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*\*/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Use the first user's message as an auto-save title when available
  const getAutoSaveTitle = (msgs, fallback) => {
    if (!Array.isArray(msgs)) return fallback || 'Auto-saved';
    const firstUser = msgs.find((m) => m && m.role === 'user' && m.content && m.content.trim());
    if (firstUser) {
      const text = firstUser.content.replace(/\s+/g, ' ').trim();
      return text.length > 80 ? `${text.slice(0, 80).trim()}...` : text;
    }
    return fallback || `Auto-saved: ${new Date().toLocaleString()}`;
  };

  // Insert or replace the latest auto-save to avoid creating many duplicates
  const upsertAutoSave = (list, autoSave) => {
    const arr = Array.isArray(list) ? [...list] : [];
    if (arr.length > 0 && arr[0] && arr[0].isAutoSave) {
      // If titles match or messages are identical, replace the latest auto-save
      try {
        const sameMessages = JSON.stringify(arr[0].messages) === JSON.stringify(autoSave.messages);
        if (arr[0].title === autoSave.title || sameMessages) {
          arr[0] = autoSave;
          return arr;
        }
      } catch (e) {
        // fallback to replacing when title matches
        if (arr[0].title === autoSave.title) {
          arr[0] = autoSave;
          return arr;
        }
      }
    }

    // Otherwise prepend new auto-save
    return [autoSave, ...arr];
  };
//https://backend-qureo-production-fab5.up.railway.app;

  const API_BASE =  'https://backend-qureo-production-fab5.up.railway.app';
  const createSymptomSessionId = () =>
    `sym-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! Let's help identify your symptoms. What are you experiencing?",
    },
  ]);
  const [input, setInput] = useState("");
  const [savedChats, setSavedChats] = useState([]);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [chatTitle, setChatTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [chatSessionId, setChatSessionId] = useState(() => {
    const existing = localStorage.getItem("symptomChatSessionId");
    if (existing && existing.trim()) return existing;
    const fresh = createSymptomSessionId();
    localStorage.setItem("symptomChatSessionId", fresh);
    return fresh;
  });
  const [backendSyncPending, setBackendSyncPending] = useState(false);
  const [selectedBubbleContext, setSelectedBubbleContext] = useState({
    messageIndex: null,
    options: [],
  });
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const backendSyncTimeoutRef = useRef(null);

  const trustStripRef = useRef(null);
  const [isTrustStripPaused, setIsTrustStripPaused] = useState(false);

  const {profile} = useAuth()
  const navigate = useNavigate();
  const location = useLocation();

  const suggestions = [
    "Headache",
    "Fever",
    "Cough",
    "Fatigue",
    "Abdominal Pain",
    "Chest Pain",
    "Back Pain",
    "Shortness of Breath",
    "Dizziness",
    "Sore Throat",
    "Joint Pain",
    "Skin Rash",
  ];

  const resetConversationState = () => {
    const saved = localStorage.getItem("savedChats");
    if (saved) {
      setSavedChats(JSON.parse(saved));
    }
    
    // Always start fresh - clear any previous chat
    localStorage.removeItem("currentChat");
    localStorage.removeItem("lastSaveTime");

    const nextSessionId = createSymptomSessionId();
    setChatSessionId(nextSessionId);
    localStorage.setItem("symptomChatSessionId", nextSessionId);
    
    // Reset to initial greeting (START NEW CHAT)
    setMessages([
      {
        role: "assistant",
        content: "Hello! Let's help identify your symptoms. What are you experiencing?",
      },
    ]);
    setInput("");
    setHasUnsavedChanges(false);
    setLastSaveTime(null);
    setShowWelcome(true);
    setBackendSyncPending(false);
    
    // Generate fresh chat title
    setChatTitle(`Chat ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);

    console.log('📝 New symptom checker session started');
  };

  const persistSymptomChatToBackend = async (messageList = messages) => {
    if (!Array.isArray(messageList) || messageList.length === 0) return;

    const userId = profile?._id || localStorage.getItem("userId") || null;
    const title = chatTitle || getAutoSaveTitle(messageList, "Symptom Check Session");

    const payload = {
      sessionId: chatSessionId,
      userId,
      title,
      lastSaveTime: new Date().toISOString(),
      messages: messageList.map((msg) => ({
        role: msg?.role,
        content: msg?.content || "",
        type: msg?.type || "question",
        options: Array.isArray(msg?.options) ? msg.options : [],
        selectedOptions: Array.isArray(msg?.selectedOptions) ? msg.selectedOptions : [],
        imageUrl: msg?.imageUrl || "",
        triage: msg?.triage ?? null,
        conditionClusters: msg?.conditionClusters ?? null,
        actionPlan: msg?.actionPlan ?? null,
      })),
      metadata: {
        source: "symptom-checker",
      },
    };

    try {
      const response = await fetch(`${API_BASE}/api/gpt/chat/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setBackendSyncPending(false);
    } catch (error) {
      console.error("Failed to sync symptom chat to backend:", error);
      setBackendSyncPending(true);
    }
  };

  // ✅ Initialize with a fresh chat whenever this page becomes active
  useEffect(() => {
    resetConversationState();
  }, [location.key]);

  useEffect(() => {
    const handlePageShow = () => {
      resetConversationState();
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // ✅ Auto-save current chat to localStorage on various triggers
  useEffect(() => {
    const autoSave = () => {
      if (messages.length > 1) { // Don't save the initial greeting only
        const now = new Date().toISOString();
        
        // Auto-save to current chat
        localStorage.setItem("currentChat", JSON.stringify(messages));
        localStorage.setItem("lastSaveTime", now);
        setLastSaveTime(now);
        setHasUnsavedChanges(true);
        
        // Auto-create a backup in saved chats if conversation has reached a milestone
        if (messages.length >= 5 && messages.length % 5 === 0) { // Every 5 messages
          const existingAutoSaves = savedChats.filter(chat => 
            chat.title && chat.title.startsWith("Auto-saved: ")
          );
          
          // Keep only the 3 most recent auto-saves
          if (existingAutoSaves.length >= 3) {
            const oldAutoSaves = existingAutoSaves.slice(3);
            const updatedChats = savedChats.filter(chat => 
              !oldAutoSaves.some(old => old.id === chat.id)
            );
            setSavedChats(updatedChats);
            localStorage.setItem("savedChats", JSON.stringify(updatedChats));
          }
          
          const autoSaveChat = {
            id: Date.now(),
            title: getAutoSaveTitle(messages, `Auto-saved: ${messages.length} messages`),
            messages: [...messages],
            sessionId: chatSessionId,
            timestamp: now,
            date: new Date().toLocaleDateString(),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isAutoSave: true
          };
          
          const updatedChats = upsertAutoSave(savedChats, autoSaveChat);
          setSavedChats(updatedChats);
          localStorage.setItem("savedChats", JSON.stringify(updatedChats));
        }
      }
    };

    // Debounce auto-save to prevent too frequent saves
    const timeoutId = setTimeout(autoSave, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [messages, savedChats, chatSessionId]);

  // Keep backend persistence in sync with the latest symptom conversation.
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;

    if (backendSyncTimeoutRef.current) {
      clearTimeout(backendSyncTimeoutRef.current);
    }

    backendSyncTimeoutRef.current = setTimeout(() => {
      persistSymptomChatToBackend(messages);
    }, 1200);

    return () => {
      if (backendSyncTimeoutRef.current) {
        clearTimeout(backendSyncTimeoutRef.current);
      }
    };
  }, [messages, chatSessionId, chatTitle, profile?._id]);

  // Retry failed backend sync automatically.
  useEffect(() => {
    if (!backendSyncPending) return;

    const retryTimer = setTimeout(() => {
      persistSymptomChatToBackend(messages);
    }, 8000);

    return () => clearTimeout(retryTimer);
  }, [backendSyncPending, messages]);

  // ✅ Auto-save on page unload (before user leaves)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && messages.length > 1) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        
        // Force save before leaving
        localStorage.setItem("currentChat", JSON.stringify(messages));
        localStorage.setItem("lastSaveTime", new Date().toISOString());
        
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, messages]);

  // ✅ Auto-save on interval (every 30 seconds)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (hasUnsavedChanges && messages.length > 1) {
        localStorage.setItem("currentChat", JSON.stringify(messages));
        localStorage.setItem("lastSaveTime", new Date().toISOString());
        console.log("Auto-saved chat (interval)");
      }
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [hasUnsavedChanges, messages]);

  // ✅ Prevent page auto-scroll jump on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // ✅ Only scroll chat to bottom when new messages arrive (not on mount)
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    setSelectedBubbleContext({ messageIndex: null, options: [] });
  }, [messages]);

  useEffect(() => {
    setShowWelcome(messages.length <= 1);
  }, [messages.length]);


  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return;

    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;

      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }

      strip.scrollBy({ left: 1, behavior: "auto" });
    }, 24);

    return () => clearInterval(interval);
  }, [isTrustStripPaused]);

  // ✅ Create a new chat
  const createNewChat = () => {
    // Auto-save current chat before creating new one
    if (hasUnsavedChanges && messages.length > 1) {
      const shouldSave = window.confirm("Do you want to save the current chat before starting a new one?");
      
      if (shouldSave) {
        saveCurrentChat();
      } else {
        // Create auto-save of current chat
        const autoSaveTitle = getAutoSaveTitle(messages, `Auto-saved before new chat: ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
        const autoSaveChat = {
          id: Date.now(),
          title: autoSaveTitle,
          messages: [...messages],
          sessionId: chatSessionId,
          timestamp: new Date().toISOString(),
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isAutoSave: true
        };
        
        const updatedChats = upsertAutoSave(savedChats, autoSaveChat);
        setSavedChats(updatedChats);
        localStorage.setItem("savedChats", JSON.stringify(updatedChats));
      }
    }
    
    // Reset to new chat
    setMessages([
      {
        role: "assistant",
        content: "Hello, Please describe your symptoms so I can help you run some possible checks.",
      },
    ]);

    const nextSessionId = createSymptomSessionId();
    setChatSessionId(nextSessionId);
    localStorage.setItem("symptomChatSessionId", nextSessionId);
    
    setInput("");
    setChatTitle(`Chat ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
    setHasUnsavedChanges(false);
    setLastSaveTime(null);
    
    // Clear current chat storage
    localStorage.removeItem("currentChat");
    localStorage.removeItem("lastSaveTime");
    
    // Show success message
    showNotification("🆕 New chat created!");
    
    // Close side panel if open
    setShowSidePanel(false);
  };

  // ✅ Save current chat to saved chats
  const saveCurrentChat = () => {
    if (messages.length <= 1) {
      alert("Please start a conversation before saving.");
      return;
    }

    setIsSaving(true);
    
    // Generate a title if not provided
    const title = chatTitle || `Chat ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    
    const newChat = {
      id: Date.now(),
      title: title,
      messages: [...messages],
      sessionId: chatSessionId,
      timestamp: new Date().toISOString(),
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isAutoSave: false
    };

    const updatedChats = [newChat, ...savedChats.filter(chat => !chat.isAutoSave)];
    setSavedChats(updatedChats);
    localStorage.setItem("savedChats", JSON.stringify(updatedChats));
    
    // Clear current chat from localStorage
    localStorage.removeItem("currentChat");
    localStorage.removeItem("lastSaveTime");
    
    // Reset messages to initial state
    setMessages([
      {
        role: "assistant",
        content: "Hello, Please describe your symptoms so I can help you run some possible checks.",
      },
    ]);

    const nextSessionId = createSymptomSessionId();
    setChatSessionId(nextSessionId);
    localStorage.setItem("symptomChatSessionId", nextSessionId);
    
    setChatTitle("");
    setHasUnsavedChanges(false);
    setLastSaveTime(null);
    setIsSaving(false);
    
    // Show success message
    showNotification("✅ Chat saved successfully!");
  };

  // ✅ Load a saved chat
  const loadChat = (chatId) => {
    const chatToLoad = savedChats.find(chat => chat.id === chatId);
    if (chatToLoad) {
      // Auto-save current chat before loading new one
      if (hasUnsavedChanges && messages.length > 1) {
        const autoSaveTitle = getAutoSaveTitle(messages, `Auto-saved before loading: ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
        const autoSaveChat = {
          id: Date.now(),
          title: autoSaveTitle,
          messages: [...messages],
          sessionId: chatSessionId,
          timestamp: new Date().toISOString(),
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isAutoSave: true
        };
        
        const updatedChats = upsertAutoSave(savedChats, autoSaveChat);
        setSavedChats(updatedChats);
        localStorage.setItem("savedChats", JSON.stringify(updatedChats));
      }
      
      setMessages(chatToLoad.messages);
      if (chatToLoad.sessionId && typeof chatToLoad.sessionId === "string") {
        setChatSessionId(chatToLoad.sessionId);
        localStorage.setItem("symptomChatSessionId", chatToLoad.sessionId);
      }
      setHasUnsavedChanges(false);
      setShowSidePanel(false);
      
      // Update current chat storage
      localStorage.setItem("currentChat", JSON.stringify(chatToLoad.messages));
      localStorage.setItem("lastSaveTime", chatToLoad.timestamp);
      setLastSaveTime(chatToLoad.timestamp);
    }
  };

  // ✅ Delete a saved chat
  const deleteChat = (chatId, e) => {
    e.stopPropagation(); // Prevent loading the chat when deleting
    if (window.confirm("Are you sure you want to delete this chat?")) {
      const updatedChats = savedChats.filter(chat => chat.id !== chatId);
      setSavedChats(updatedChats);
      localStorage.setItem("savedChats", JSON.stringify(updatedChats));
      showNotification("🗑️ Chat deleted");
    }
  };

  // ✅ Clear all chats
  const clearAllChats = () => {
    if (window.confirm("Are you sure you want to delete all saved chats? This will also clear auto-saved chats.")) {
      setSavedChats([]);
      localStorage.removeItem("savedChats");
      localStorage.removeItem("currentChat");
      localStorage.removeItem("lastSaveTime");
      setHasUnsavedChanges(false);
      setLastSaveTime(null);
      showNotification("🧹 All chats cleared");
    }
  };

  // ✅ Restore from last auto-save
  const restoreAutoSave = () => {
    const currentChat = localStorage.getItem("currentChat");
    if (currentChat) {
      try {
        const parsedChat = JSON.parse(currentChat);
        setMessages(parsedChat);
        setHasUnsavedChanges(true);
        showNotification("↩️ Restored from auto-save!");
      } catch (error) {
        console.error("Error restoring auto-save:", error);
      }
    }
  };

  // ✅ Show notification
  const showNotification = (message) => {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--accent);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      animation: slideIn 0.3s ease;
      display: flex;
      align-items: center;
      gap: 10px;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = "slideOut 0.3s ease";
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const getSymptomFlowContext = (messageList = messages) => {
    const safeMessages = Array.isArray(messageList) ? messageList : [];
    const userMessages = safeMessages.filter((msg) => msg.role === "user");
    const followupAssistantMessages = safeMessages.filter(
      (msg, idx) => msg.role === "assistant" && idx > 0
    );
    const hasCareConnection = followupAssistantMessages.some((msg) =>
      /doctor|consult|clinic|hospital|emergency|urgent care|call\s+\d+/i.test(msg.content || "")
    );

    let stage = 1;
    if (userMessages.length > 0) stage = 2;
    if (followupAssistantMessages.length > 0) stage = 3;
    if (hasCareConnection) stage = 4;

    const labels = {
      1: "understand_symptoms",
      2: "assess_urgency",
      3: "recommend_next_steps",
      4: "connect_to_care",
    };

    return { stage, label: labels[stage] };
  };

  const getUrgencyLabel = (triage, content = "") => {
    if (typeof triage === "string" && triage.trim()) return triage.trim();
    if (triage && typeof triage === "object") {
      const candidate = triage.level || triage.urgency || triage.severity || triage.priority;
      if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    }

    const lower = String(content).toLowerCase();
    if (/emergency|severe|critical|urgent|immediately/.test(lower)) return "High priority";
    if (/mild|monitor|home care|self-care/.test(lower)) return "Low priority";
    return "Moderate priority";
  };

  const getNextSteps = (actionPlan, content = "") => {
    const list = [];

    const pushItem = (value) => {
      if (!value || typeof value !== "string") return;
      const normalized = value.replace(/^[\-•*\d.)\s]+/, "").trim();
      if (normalized && normalized.length < 220) list.push(normalized);
    };

    if (Array.isArray(actionPlan)) {
      actionPlan.forEach((item) => {
        if (typeof item === "string") pushItem(item);
        else if (item && typeof item === "object") {
          pushItem(item.step || item.action || item.recommendation || item.label);
        }
      });
    } else if (actionPlan && typeof actionPlan === "object") {
      ["nextSteps", "recommendations", "actions", "plan"].forEach((key) => {
        const val = actionPlan[key];
        if (Array.isArray(val)) {
          val.forEach((entry) => {
            if (typeof entry === "string") pushItem(entry);
            else if (entry && typeof entry === "object") {
              pushItem(entry.step || entry.action || entry.recommendation || entry.label);
            }
          });
        } else if (typeof val === "string") {
          pushItem(val);
        }
      });
    } else if (typeof actionPlan === "string") {
      actionPlan.split("\n").forEach(pushItem);
    }

    if (list.length === 0 && typeof content === "string") {
      content
        .split("\n")
        .filter((line) => /^\s*(?:[-•*]|\d+[.)])\s+/.test(line))
        .slice(0, 3)
        .forEach(pushItem);
    }

    const unique = Array.from(new Set(list));
    if (unique.length > 0) return unique.slice(0, 3);

    return [
      "Track your symptoms for the next 24 hours.",
      "Rest, hydrate, and avoid known symptom triggers.",
      "Seek medical care if symptoms worsen.",
    ];
  };

  const MIN_USER_RESPONSES_BEFORE_CONCLUSION = 3;
  const MIN_ASSISTANT_QUESTION_TURNS = 2;

  const getConversationQuestionStats = (messageList = messages) => {
    const safeMessages = Array.isArray(messageList) ? messageList : [];

    const userResponses = safeMessages.filter(
      (msg) => msg?.role === "user" && !msg?.isUploading && (msg?.content?.trim() || msg?.imageUrl)
    ).length;

    const assistantQuestionTurns = safeMessages.filter((msg, idx) => {
      if (msg?.role !== "assistant") return false;
      if (!Array.isArray(msg?.options) || msg.options.length === 0) return false;
      if (idx === 0) return false;

      const lower = String(msg?.content || "").toLowerCase();
      return !/retry|start over|error connecting/.test(lower);
    }).length;

    return { userResponses, assistantQuestionTurns };
  };

  const isConclusionLikeMessage = (assistantBlock) => {
    if (!assistantBlock || assistantBlock.role !== "assistant") return false;

    const type = String(assistantBlock.type || "").toLowerCase();
    if (["recommendation", "final", "conclusion", "summary", "action_plan", "connect_to_care"].includes(type)) {
      return true;
    }

    if (assistantBlock.triage || assistantBlock.actionPlan) return true;

    const lower = String(assistantBlock.content || "").toLowerCase();
    return /in summary|final recommendation|next steps|consult|doctor|pharmacy|care plan/.test(lower);
  };

  const buildRequiredFollowUpQuestion = (messageList = messages) => {
    const { userResponses } = getConversationQuestionStats(messageList);

    if (userResponses < 2) {
      return {
        role: "assistant",
        type: "question",
        content:
          "To help diagnose your symptoms better, I need a few more details. How long have you had these symptoms?",
        options: [
          "Less than 24 hours",
          "1 to 3 days",
          "4 to 7 days",
          "More than 1 week",
        ],
      };
    }

    if (userResponses < 3) {
      return {
        role: "assistant",
        type: "question",
        content:
          "Thanks. How severe are your symptoms right now?",
        options: ["Mild", "Moderate", "Severe", "Very severe"],
      };
    }

    return {
      role: "assistant",
      type: "question",
      content:
        "Are you also experiencing any of these related symptoms? You can select one or more.",
      options: [
        "Fever or chills",
        "Nausea or vomiting",
        "Shortness of breath",
        "Dizziness or weakness",
      ],
    };
  };

  const enforceQuestioningFlow = (assistantBlock, messageList = messages) => {
    if (!isConclusionLikeMessage(assistantBlock)) return assistantBlock;

    const { userResponses, assistantQuestionTurns } = getConversationQuestionStats(messageList);
    const hasEnoughContext =
      userResponses >= MIN_USER_RESPONSES_BEFORE_CONCLUSION &&
      assistantQuestionTurns >= MIN_ASSISTANT_QUESTION_TURNS;

    if (hasEnoughContext) return assistantBlock;

    return buildRequiredFollowUpQuestion(messageList);
  };

  const getConditionHint = (conditionClusters) => {
    if (Array.isArray(conditionClusters) && conditionClusters.length > 0) {
      const first = conditionClusters[0];
      if (typeof first === "string" && first.trim()) return first.trim();
      if (first && typeof first === "object") {
        const label = first.name || first.condition || first.label || first.title;
        if (typeof label === "string" && label.trim()) return label.trim();
      }
    }

    if (conditionClusters && typeof conditionClusters === "object") {
      const candidate =
        conditionClusters.primary ||
        conditionClusters.topCondition ||
        conditionClusters.label ||
        conditionClusters.name;
      if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    }

    if (typeof conditionClusters === "string" && conditionClusters.trim()) {
      return conditionClusters.trim();
    }

    return "a mild to moderate condition";
  };

  const getGeneralizedReport = (msg, content = "") => {
    const urgency = getUrgencyLabel(msg?.triage, content).toLowerCase();
    const conditionHint = getConditionHint(msg?.conditionClusters);
    const nextSteps = getNextSteps(msg?.actionPlan, content);
    const firstStep = nextSteps[0] || "monitor your symptoms closely";

    return `Base on your symptom, this may be related to ${conditionHint} with a ${urgency} concern level. Please ${firstStep.toLowerCase()} and seek medical care quickly if symptoms become worse.`;
  };

  const getRecommendedDoctorSearch = (msg, content = "") => {
    const text = `${getConditionHint(msg?.conditionClusters)} ${content}`.toLowerCase();
    if (/heart|chest|cardiac|palpitation/.test(text)) return "Cardiologist";
    if (/headache|migraine|seizure|neurolog/.test(text)) return "Neurologist";
    if (/stomach|abdominal|digestive|diarrh|vomit/.test(text)) return "Gastroenterologist";
    if (/skin|rash|eczema|acne/.test(text)) return "Dermatologist";
    if (/pregnan|menstrual|gynec|women/.test(text)) return "Gynecologist";
    if (/child|baby|infant|pediatric/.test(text)) return "Pediatrician";
    if (/anxiety|depress|mental|stress/.test(text)) return "Mental Health";
    return "General Practitioner";
  };

  const shouldShowCarePathwayAtEnd = (msg, messageIndex, messageList = messages) => {
    if (!msg || msg.role !== "assistant") return false;
    if (!Array.isArray(messageList) || messageList.length === 0) return false;

    // Show only on the latest assistant response in the conversation.
    if (messageIndex !== messageList.length - 1) return false;

    // Do not show while there are still selectable question options.
    if (Array.isArray(msg.options) && msg.options.length > 0) return false;

    const { userResponses, assistantQuestionTurns } = getConversationQuestionStats(messageList);
    if (userResponses < MIN_USER_RESPONSES_BEFORE_CONCLUSION) return false;
    if (assistantQuestionTurns < MIN_ASSISTANT_QUESTION_TURNS) return false;

    const type = String(msg.type || "").toLowerCase();
    if (["recommendation", "final", "conclusion", "summary", "action_plan", "connect_to_care"].includes(type)) {
      return true;
    }

    if (msg.triage || msg.actionPlan) return true;

    const lower = String(msg.content || "").toLowerCase();
    return /next steps|recommend|consult|doctor|pharmacy|care/.test(lower);
  };

  const sendMessage = async (overrideInput = null) => {
    // Check if there are selected bubbles
    if (!overrideInput && selectedBubbleContext.options.length > 0) {
      await sendBubbleSelection(selectedBubbleContext.options);
      return;
    }

    const messageText = typeof overrideInput === "string" ? overrideInput : input;
    if (!messageText.trim()) return;

    const userMsg = { role: "user", content: messageText };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setHasUnsavedChanges(true);
    const flowContext = getSymptomFlowContext(updatedMessages);

    try {
      const response = await fetch(`${API_BASE}/api/gpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages,
          symptomFlowStage: flowContext.stage,
          symptomFlowLabel: flowContext.label,
        }),
      });

      const data = await response.json();
      const aiReply = data.message || "I couldn't process your input, please try again.";
      
      // PRIORITY: Use backend JSON options if provided
      let rawOptions = normalizeBackendOptions(data.options);
      console.log("gpt result", data);
      console.log('📊 Backend options received:', rawOptions.length > 0 ? rawOptions : 'NONE - will try text extraction');
      //console.log('📨 Backend response:', { message: aiReply.substring(0, 80), optionsCount: rawOptions.length });

      const extractedOptions = extractOptionsFromMessage(aiReply);

      // Prefer extracted options when backend sends only generic Yes/No, or when backend is empty.
      if (rawOptions.length === 0 || (isGenericBinaryOptions(rawOptions) && extractedOptions.length >= 3)) {
        console.log('⚠️ No backend options, trying text extraction...');
        rawOptions = extractedOptions;
      }
      
      // Deduplicate and limit to 6
      const sanitizedOptions = Array.from(new Set(rawOptions)).slice(0, 6);
      
      console.log('✅ Final options:', sanitizedOptions);
      
      const messageType = data.type || "question";

      // include extra structured info if present
      const recommendationBlock = {
        role: "assistant",
        content: aiReply,
        type: messageType,
        options: sanitizedOptions,
        triage: data.triage,
        conditionClusters: data.conditionClusters,
        actionPlan: data.actionPlan,
      };
      const nextAssistantBlock = enforceQuestioningFlow(recommendationBlock, updatedMessages);
      setMessages((prev) => [...prev, nextAssistantBlock]);
      
      // Auto-save after AI response
      setTimeout(() => {
        localStorage.setItem("currentChat", JSON.stringify([...updatedMessages, nextAssistantBlock]));
        localStorage.setItem("lastSaveTime", new Date().toISOString());
        setLastSaveTime(new Date().toISOString());
      }, 500);
      
    } catch (error) {
      console.error("Error:", error);
      const errorMsg = {
        role: "assistant",
        content: "There was an error connecting to the server. Please try again.",
        options: ["Retry", "Start Over"],
      };
      setMessages((prev) => [...prev, errorMsg]);
    }
  };

  const sendBubbleSelection = async (selectedOptions) => {
    if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) return;

    const userSelectionText = selectedOptions.length === 1
      ? selectedOptions[0]
      : `Selected symptoms: ${selectedOptions.join(", ")}`;

    console.log('🔘 User submitted options:', selectedOptions);
    const userMsg = { role: "user", content: userSelectionText, selectedOptions };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setHasUnsavedChanges(true);
    setSelectedBubbleContext({ messageIndex: null, options: [] });
    const flowContext = getSymptomFlowContext(updatedMessages);

    try {
      const response = await fetch(`${API_BASE}/api/gpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages,
          symptomFlowStage: flowContext.stage,
          symptomFlowLabel: flowContext.label,
        }),
      });

      const data = await response.json();
      const aiReply = data.message || "I couldn't process that, please try again.";
      
      // PRIORITY: Use backend JSON options if provided
      let rawOptions = normalizeBackendOptions(data.options);
      
      console.log('🔘 User clicked:', selectedOptions);
      console.log("data", data )
      console.log('📨 Backend response: options=' + rawOptions.length);

      const extractedOptions = extractOptionsFromMessage(aiReply);

      // Prefer extracted options when backend sends only generic Yes/No, or when backend is empty.
      if (rawOptions.length === 0 || (isGenericBinaryOptions(rawOptions) && extractedOptions.length >= 3)) {
        console.log('⚠️ No backend options, trying text extraction...');
        rawOptions = extractedOptions;
      }
      
      // Deduplicate and limit to 6
      const sanitizedOptions = Array.from(new Set(rawOptions)).slice(0, 6);
      
      console.log('✅ Final options:', sanitizedOptions);
      
      const messageType = data.type || "question";
      
      // Capture optional structured fields
      const triage = data.triage;
      const conditionClusters = data.conditionClusters;
      const actionPlan = data.actionPlan;

      const responseBlock = {
        role: "assistant",
        content: aiReply,
        type: messageType,
        options: sanitizedOptions,
        triage,
        conditionClusters,
        actionPlan,
      };
      const nextAssistantBlock = enforceQuestioningFlow(responseBlock, updatedMessages);
      setMessages((prev) => [...prev, nextAssistantBlock]);

      // Auto-save
      setTimeout(() => {
        localStorage.setItem("currentChat", JSON.stringify([...updatedMessages, nextAssistantBlock]));
        localStorage.setItem("lastSaveTime", new Date().toISOString());
        setLastSaveTime(new Date().toISOString());
      }, 500);
    } catch (error) {
      console.error("Error:", error);
      const errorMsg = {
        role: "assistant",
        content: "There was an error processing your selection.",
        options: ["Retry", "Start Over"],
      };
      setMessages((prev) => [...prev, errorMsg]);
    }
  };

  // Handle selecting/deselecting bubble options (multi-select)
  const handleBubbleClick = (option, messageIndex) => {
    setSelectedBubbleContext((prev) => {
      const sameMessage = prev.messageIndex === messageIndex;
      const current = sameMessage ? prev.options : [];
      const exists = current.includes(option);
      const nextOptions = exists
        ? current.filter((item) => item !== option)
        : [...current, option];

      // Update input field with selected options
      if (nextOptions.length > 0) {
        const displayText = nextOptions.length === 1
          ? nextOptions[0]
          : `Selected symptoms: ${nextOptions.join(", ")}`;
        setInput(displayText);
      } else {
        setInput("");
      }

      return {
        messageIndex,
        options: nextOptions,
      };
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Upload file to Cloudinary
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "profile_preset");

    console.log('📤 Uploading to Cloudinary:', file.name);
    
    const res = await fetch(`https://api.cloudinary.com/v1_1/dp09q5por/auto/upload`, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    
    if (!data.secure_url) {
      console.error('❌ Cloudinary upload failed:', data);
      throw new Error('Failed to upload file');
    }
    
    console.log('✅ Uploaded to:', data.secure_url);
    return data.secure_url;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show uploading message
    const uploadingMsg = { role: "user", content: "📎 Uploading image...", isUploading: true };
    setMessages((prev) => [...prev, uploadingMsg]);

    try {
      // Upload to Cloudinary
      const fileUrl = await uploadToCloudinary(file);

      // Render uploaded image inline without exposing original filename in chat.
      const attachedMsg = {
        role: "user",
        content: "",
        imageUrl: fileUrl,
        fileName: "",
      };

      const baseMessages = [...messages, attachedMsg];
      setMessages((prev) => {
        const withoutUploadPlaceholder = prev.filter((msg) => !msg?.isUploading);
        return [...withoutUploadPlaceholder, attachedMsg];
      });
      
      setHasUnsavedChanges(true);
      
      // 1) Analyze uploaded image bytes directly (works for X-rays and symptom photos)
      // 2) Fallback to GPT vision chat route if direct analysis fails
      const analyzed = await analyzeImageWithSymptomEngine(file, file.name, baseMessages);
      if (!analyzed) {
        await sendFileForAnalysis(fileUrl, file.name, baseMessages);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setMessages((prev) => {
        const withoutUploadPlaceholder = prev.filter((msg) => !msg?.isUploading);
        return [...withoutUploadPlaceholder, {
          role: "assistant",
          content: "Failed to upload file. Please try again.",
          options: ["Retry", "Cancel"]
        }];
      });
    } finally {
      e.target.value = "";
    }
  };

  const analyzeImageWithSymptomEngine = async (file, fileName, baseMessages = messages) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const flowContext = getSymptomFlowContext(baseMessages);
      formData.append("symptomFlowStage", String(flowContext.stage));
      formData.append("symptomFlowLabel", flowContext.label);

      const response = await fetch(`${API_BASE}/api/gpt/analyze-image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      if (!result || typeof result !== "object") {
        return false;
      }

      const recommendationBlock = {
        role: "assistant",
        content: result.message || "I analyzed your uploaded image.",
        type: result.type || "recommendation",
        options: Array.isArray(result.options) ? result.options : [
          "What exactly looks abnormal?",
          "Could this be serious?",
          "What should I do next?",
        ],
        triage: result.triage,
        conditionClusters: result.conditionClusters,
        actionPlan: result.actionPlan,
      };

      const nextAssistantBlock = enforceQuestioningFlow(recommendationBlock, baseMessages);
      const updatedMessages = [...baseMessages, nextAssistantBlock];
      setMessages(updatedMessages);
      setHasUnsavedChanges(true);
      localStorage.setItem("currentChat", JSON.stringify(updatedMessages));
      localStorage.setItem("lastSaveTime", new Date().toISOString());
      setLastSaveTime(new Date().toISOString());

      return true;
    } catch (error) {
      console.error("Symptom image-engine analysis failed:", error);
      return false;
    }
  };

  // Send file for AI analysis
  const sendFileForAnalysis = async (imageUrl, fileName, baseMessages = messages) => {
    const userMsg = {
      role: "user",
      content: "Please analyze this uploaded medical image in detail. Describe what you see, identify findings or abnormalities, and share whether this may need urgent medical care.",
      imageUrl: imageUrl
    };
    
    console.log('📸 Sending image for analysis:', { fileName, imageUrl: imageUrl.substring(0, 50) + '...' });
    
    const updatedMessages = [...baseMessages, userMsg];
    const flowContext = getSymptomFlowContext(updatedMessages);

    try {
      const response = await fetch(`${API_BASE}/api/gpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: updatedMessages,
          symptomFlowStage: flowContext.stage,
          symptomFlowLabel: flowContext.label,
        }),
      });

      const data = await response.json();
      console.log('📥 AI Response:', { status: response.status, hasMessage: !!data.message, messageLength: data.message?.length });
      
      const aiReply = data.message || "I couldn't process the image, please try again.";
      
      let rawOptions = normalizeBackendOptions(data.options);
      const extractedOptions = extractOptionsFromMessage(aiReply);

      if (rawOptions.length === 0 || (isGenericBinaryOptions(rawOptions) && extractedOptions.length >= 3)) {
        rawOptions = extractedOptions;
      }
      
      const sanitizedOptions = Array.from(new Set(rawOptions)).slice(0, 6);
      const messageType = data.type || "question";

      const recommendationBlock = {
        role: "assistant",
        content: aiReply,
        type: messageType,
        options: sanitizedOptions,
        triage: data.triage,
        conditionClusters: data.conditionClusters,
        actionPlan: data.actionPlan,
      };

      const nextAssistantBlock = enforceQuestioningFlow(recommendationBlock, updatedMessages);
      setMessages((prev) => [...prev, nextAssistantBlock]);
      
      setTimeout(() => {
        localStorage.setItem("currentChat", JSON.stringify([...updatedMessages, nextAssistantBlock]));
        localStorage.setItem("lastSaveTime", new Date().toISOString());
        setLastSaveTime(new Date().toISOString());
      }, 500);
      
    } catch (error) {
      console.error("Error:", error);
      const errorMsg = {
        role: "assistant",
        content: "There was an error analyzing the image. Please try again.",
        options: ["Retry", "Start Over"],
      };
      setMessages((prev) => [...prev, errorMsg]);
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    if (isListening) {
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    setIsListening(true);
    recognition.start();
  };

  const handleBack = () => {
    navigate("/home");
  };

  const popularSymptoms = [
    { label: "Fever", Icon: FeverSymptomIcon },
    { label: "Headache", Icon: HeadacheSymptomIcon },
    { label: "Cough", Icon: CoughSymptomIcon },
    { label: "Stomach pain", Icon: StomachPainSymptomIcon },
    { label: "Vomiting", Icon: VomitingSymptomIcon },
    { label: "Body pain", Icon: BodyPainSymptomIcon },
  ];

  const quickSend = (symptomText) => {
    setInput(symptomText);
    setTimeout(() => sendMessage(symptomText), 0);
  };

  return (
    <div className="symptom-page">
      {showSidePanel && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100vh",
              width: 280,
              background: "#fff",
              zIndex: 100,
              boxShadow: "4px 0 12px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: 16,
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong>Chat History</strong>
              <button
                onClick={() => setShowSidePanel(false)}
                style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer" }}
              >
                x
              </button>
            </div>

            <button
              onClick={() => {
                createNewChat();
                setShowSidePanel(false);
              }}
              style={{
                margin: 16,
                padding: "10px 14px",
                borderRadius: 10,
                border: "none",
                background: "var(--primary)",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              + New Chat
            </button>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {savedChats.length === 0 && <p style={{ padding: 16, color: "#777" }}>No saved chats yet</p>}

              {savedChats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => {
                    loadChat(chat.id);
                    setShowSidePanel(false);
                  }}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "pointer",
                  }}
                >
                  <strong style={{ display: "block" }}>{chat.title}</strong>
                  <small style={{ color: "#888" }}>{chat.messages.length} messages</small>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <header>
        <FeatureHeader
          title="Symptom Checker"
          subtitle="AI-powered guidance, reviewed by clinicians"
          onBack={() => navigate(-1)}
          rightSlot={(
            <button
              onClick={() => setShowSidePanel(true)}
              className="symptom-icon-btn"
              aria-label="Show saved symptom checks"
              title="Saved symptom checks"
              style={{ marginRight: 0 }}
            >
              <i className="fi fi-br-menu-burger" aria-hidden="true"></i>
            </button>
          )}
          subtitleStyle={{ color: "color-mix(in srgb, var(--on-bg) 70%, transparent)" }}
        />
      </header>

      <div className="symptom-scroll-area">
        {showWelcome ? (
          <>
          {/*
            <section
              ref={trustStripRef}
              className="trust-strip"
              onMouseEnter={() => setIsTrustStripPaused(true)}
              onMouseLeave={() => setIsTrustStripPaused(false)}
              onTouchStart={() => setIsTrustStripPaused(true)}
              onTouchEnd={() => setIsTrustStripPaused(false)}
            >
              <div className="trust-track">
                {[...trustHighlights, ...trustHighlights].map((item, index, list) => (
                  <React.Fragment key={`${item.title}-${index}`}>
                    <div className="trust-item">
                      <div className="trust-item-icon-wrap">
                        <item.Icon size={13} color="var(--primary)" />
                      </div>
                      <div className="trust-item-copy">
                        <span className="trust-item-title">{item.title}</span>
                        <span className="trust-item-subtitle">{item.subtitle}</span>
                      </div>
                    </div>
                    {index < list.length - 1 && <div className="trust-strip-divider"></div>}
                  </React.Fragment>
                ))}
              </div>
            </section>
            */}
            <section className="hero-card">
              <div className="hero-card-copy">
                <p className="hero-eyebrow">
                  Hi there! <span role="img" aria-label="waving hand">👋</span>
                </p>
                <h3>
                  I'm <span className="hero-name">Qureo</span>
                </h3>
                <p className="hero-description">
                  Describe symptoms in your own words
                  <br />
                  or speak in your local language.
                </p>
                <div className="hero-support-note">
                  <span className="hero-support-icon" aria-hidden="true">
                    <User size={14} strokeWidth={2.2} />
                  </span>
                  <span>Clinician support available if needed</span>
                </div>
              </div>
            </section>

            <section className="symptom-section">
              <div className="section-head">
                <h6>Common symptoms</h6>
              </div>
              <div className="symptom-grid">
                {popularSymptoms.map((item, index) => (
                  <button key={`${item.label}-${index}`} className="symptom-chip" onClick={() => quickSend(item.label)}>
                    <span className="chip-icon">
                      {item.Icon ? <item.Icon /> : <img src={item.icon} alt={item.label} className="chip-icon-image" />}
                    </span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              <div className="symptom-example-card" aria-label="Example symptom phrase">
                <span className="symptom-example-icon" aria-hidden="true">
                  <Lightbulb size={18} strokeWidth={2.1} />
                </span>
                <div className="symptom-example-copy">
                  <p>You can type symptoms like:</p>
                  <strong>"fever and body pain"</strong>
                </div>
              </div>
            </section>

          </>
        ) : (
          <section className="symptom-section">
            <div className="conversation-feed">
              {messages.map((msg, index) => {
                const messageIndex = index;
                const useSimpleYesNo = msg.role === "assistant" && isYesNoOptionSet(msg.options);
                const displayContent =
                  msg.role === "assistant" ? cleanAssistantResponseText(msg.content) : msg.content;
                return (
                  <React.Fragment key={`${msg.role}-${index}`}>
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`chat-bubble ${msg.role === "user" ? "user" : "assistant"}${msg.imageUrl && !msg.content ? " has-image-only" : ""}`}
                    >
                      {msg.imageUrl && (
                        <div className="chat-image-wrap">
                          <img src={msg.imageUrl} alt="Uploaded medical" className="chat-inline-image" />
                        </div>
                      )}
                      {displayContent ? <span>{displayContent}</span> : null}
                    </motion.div>
                    {msg.role === "assistant" && Array.isArray(msg.options) && msg.options.length > 0 && (
                      <div className={`response-options${useSimpleYesNo ? " binary" : ""}`}>
                        {msg.options.map((opt, optIndex) => {
                          const isSelected =
                            selectedBubbleContext.messageIndex === messageIndex &&
                            selectedBubbleContext.options.includes(opt);
                          return (
                            <button
                              key={`${opt}-${optIndex}`}
                              type="button"
                              className={`response-option-card${useSimpleYesNo ? " compact" : ""}${isSelected ? " selected" : ""}`}
                              onClick={() => handleBubbleClick(opt, messageIndex)}
                            >
                              <span className="response-option-text">{useSimpleYesNo ? simplifyBinaryOptionLabel(opt) : opt}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {shouldShowCarePathwayAtEnd(msg, messageIndex, messages) && (
                      <div className="care-pathway-card">
                        <div className="care-pathway-section">
                          <span className="care-pathway-title">Assess urgency</span>
                          <p className="care-pathway-urgency">{getUrgencyLabel(msg.triage, displayContent)}</p>
                        </div>

                        <div className="care-pathway-section">
                          <span className="care-pathway-title">Next steps recommendation</span>
                          <ul className="care-pathway-list">
                            {getNextSteps(msg.actionPlan, displayContent).map((step, stepIndex) => (
                              <li key={`${messageIndex}-step-${stepIndex}`}>{step}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="care-pathway-section">
                          <span className="care-pathway-title">Generalized report</span>
                          <p className="care-pathway-urgency" style={{ marginTop: "0.4rem" }}>
                            {getGeneralizedReport(msg, displayContent)}
                          </p>
                          <div className="care-pathway-actions">
                            <button
                              type="button"
                              className="care-action-btn doctor"
                              onClick={() => navigate(`/all-doctors?specialty=${encodeURIComponent(getRecommendedDoctorSearch(msg, displayContent))}`)}
                            >
                              Find a Doctor
                            </button>
                            <button
                              type="button"
                              className="care-action-btn pharmacy"
                              onClick={() => navigate("/epharmacy")}
                            >
                              Check Pharmacy
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              <div ref={chatEndRef} />
            </div>
          </section>
        )}
      </div>
 
      {isListening && (
        <div className="listening-indicator">
          <span className="dot" />
          Listening... speak now
        </div>
      )}

      <div className="composer-shell">
        <div className="composer-row">
          <button className="composer-icon soft" onClick={handleAttachClick} aria-label="Attach file">
            <Paperclip size={16} color="#5a2ec4" strokeWidth={2} />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type or speak your symptoms..."
            className="composer-input"
          />
          <button
            className="composer-icon solid"
            onClick={() => sendMessage()}
            disabled={!input.trim() && selectedBubbleContext.options.length === 0}
            aria-label="Send message"
          >
            <Send size={16} color="#fff" strokeWidth={2} />
          </button>
          <button
            className={`composer-icon solid ${isListening ? "active" : ""}`}
            onClick={handleVoiceInput}
            aria-label="Start voice input"
          >
            <Mic size={16} color="#fff" strokeWidth={2} />
          </button>
        </div>
        <div className="composer-toggle-row">
          <button className="toggle-btn active" onClick={handleVoiceInput}>
            <Mic size={15} color="#5a2ec4" strokeWidth={2} /> Voice
          </button>
          <button className="toggle-btn" onClick={() => document.querySelector(".composer-input")?.focus()}>
            <Send size={15} color="#5a5070" strokeWidth={2} /> Type
          </button>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*,.pdf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      <span className="medical-disclaimer">This is not a substitute for professional medical advice.</span>
      <span className="hipaa-note">HIPAA-compliant</span>
    </div>
  );

  
};

export default SymptomChat;
